<template>
  <space-selector
    :open="open"
    :value="selectedSpaces"
    :read-only="readOnly"
    :property-label="spaceProperty.label"
    full-width
    multiple
    wrap
    :truncate="5"
    @update:open="emit('update:open', $event)"
    @update:value="updateSelectedSpaces"
  >
    <template #trigger>
      <slot name="trigger" />
    </template>
    <template
      v-if="!isEmpty"
      #overlay-header
    >
      <m-content :padding-top="4">
        <m-content :padding-x="5">
          <quick-filter-header
            :label="scopeItem.text"
            @delete="emit('delete')"
            @add-to-advanced="emit('addToAdvanced')"
          >
            <template #op>
              <m-select
                :value="selectedType"
                :items="operatorTypes"
                :read-only="readOnly"
                xs
                hide-border
                light
                :trigger-style="{ textTransform: 'lowercase' }"
                @input="updateSelectedType"
              />
            </template>
          </quick-filter-header>
        </m-content>
      </m-content>
    </template>
    <template
      v-else
      #overlay
    >
      <m-card padding-xxs>
        <m-content :padding-x="2">
          <quick-filter-header
            :label="scopeItem.text"
            @delete="emit('delete')"
            @add-to-advanced="emit('addToAdvanced')"
          >
            <template #op>
              <m-select
                :value="selectedType"
                :items="operatorTypes"
                :read-only="readOnly"
                xs
                hide-border
                light
                :trigger-style="{ textTransform: 'lowercase' }"
                @input="updateSelectedType"
              />
            </template>
          </quick-filter-header>
        </m-content>
      </m-card>
    </template>
  </space-selector>
</template>

<script setup>
import QuickFilterHeader from '@/components/filter/quick/QuickFilterHeader.vue';
import SpaceSelector from '@/components/spaces/SpaceSelector.vue';
import useProperties from '@/composables/property/property';
import useScopeItem from '@/composables/filter/scope-item';
import { AND, EMPTY, NOT, NOT_EMPTY } from '@/lib/filter/scope-tree';
import { computed } from 'vue';
import { propertyType } from 'shared/constants.json';

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  scopeItem: {
    type: Object,
    required: true,
  },
  node: {
    type: Object,
    required: true,
  },
  spaceProperty: {
    type: Object,
    default: null,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  maxTagTextLength: {
    type: Number,
    default: 0,
  },
  hideIsEmpty: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['update:open', 'update:scope', 'update:not-operator', 'delete', 'addToAdvanced']);

const { operatorTypes: getOperatorTypes } = useScopeItem();
const operatorTypes = getOperatorTypes(propertyType.user, { hideEmpty: props.hideIsEmpty });

const isNot = computed(() => props.node.op === NOT);
const scope = computed(() => props.node.children[0].scope);
const isEmpty = computed(() => scope.value.isEmpty);

const propertySvc = useProperties();
const property = computed(() => propertySvc.selectSingle(props.scopeItem.property.uid));

const spaceProperty = computed(() => {
  if (props.spaceProperty !== null) {
    return props.spaceProperty;
  }
  return property.value;
});

const selectedType = computed(() => {
  if (isNot.value) {
    return isEmpty.value ? NOT_EMPTY : NOT;
  }
  return isEmpty.value ? EMPTY : AND;
});
const updateSelectedType = (value) => {
  if (![NOT, NOT_EMPTY].includes(value)) {
    emit('update:not-operator', AND);
  } else {
    emit('update:not-operator', NOT);
  }
  if ([EMPTY, NOT_EMPTY].includes(value)) {
    emit('update:scope', { ...scope.value, isEmpty: true });
    return;
  }
  emit('update:scope', { ...scope.value, isEmpty: false });
};

const selectedSpaces = computed(() => scope.value.spaces);
const updateSelectedSpaces = (value) => {
  if (JSON.stringify(selectedSpaces.value) === JSON.stringify(value)) {
    return;
  }
  emit('update:scope', { ...scope.value, spaces: value });
};

</script>

<style scoped lang="scss">
</style>
